/**
 * Shopify custom element section.
 *
 * Liquid renders <figma-faq-accordion> and loads this file with `defer`, so the browser upgrades
 * the element and this class owns init / teardown. The `customElements.get` guard is required:
 * the section can be placed more than once on a page, which emits the same asset URL twice, and
 * a tag name cannot be redefined once registered.
 *
 * The open/close animation lives in CSS (grid-template-rows 0fr -> 1fr), so nothing here has to
 * measure or hard-code a pixel height.
 */
class FigmaFaqAccordion extends HTMLElement {
  connectedCallback() {
    // Moving an already upgraded node within the document re-runs connectedCallback.
    if (this.dataset.faqReady) {
      return;
    }

    this.dataset.faqReady = 'true';

    this.items = Array.from(this.querySelectorAll('[data-faq-item]'));
    this.allowMultiple = this.dataset.allowMultiple === 'true';

    // One delegated listener per section instead of two per item.
    this.handleClick = (event) => {
      const toggle = event.target.closest('[data-faq-toggle]');

      if (!toggle || !this.contains(toggle)) {
        return;
      }

      event.preventDefault();
      this.toggleItem(toggle.closest('[data-faq-item]'));
    };

    this.addEventListener('click', this.handleClick);

    this.initViewMore();
  }

  disconnectedCallback() {
    if (this.handleClick) {
      this.removeEventListener('click', this.handleClick);
      this.handleClick = null;
    }

    if (this.viewMoreButton && this.handleViewMoreClick) {
      this.viewMoreButton.removeEventListener('click', this.handleViewMoreClick);
      this.handleViewMoreClick = null;
    }

    if (this.mobileQuery && this.handleMediaChange) {
      this.mobileQuery.removeEventListener('change', this.handleMediaChange);
      this.mobileQuery = null;
      this.handleMediaChange = null;
    }
  }

  toggleItem(item) {
    if (!item) {
      return;
    }

    const willOpen = item.dataset.open !== 'true';

    if (willOpen && !this.allowMultiple) {
      this.items.forEach((other) => {
        if (other !== item) {
          this.setItemOpen(other, false);
        }
      });
    }

    this.setItemOpen(item, willOpen);
  }

  setItemOpen(item, open) {
    const state = open ? 'true' : 'false';

    item.dataset.open = state;

    const toggle = item.querySelector('[data-faq-toggle]');
    const panel = item.querySelector('[data-faq-panel]');

    if (toggle) {
      toggle.setAttribute('aria-expanded', state);
    }

    if (panel) {
      panel.dataset.open = state;
    }
  }

  initViewMore() {
    this.viewMoreButton = this.querySelector('[data-view-more-button]');

    if (!this.viewMoreButton) {
      return;
    }

    this.viewMoreThreshold = parseInt(this.dataset.viewMoreThreshold, 10) || 5;
    this.viewMoreLabel = this.viewMoreButton.dataset.viewMoreLabel || 'View more';
    this.viewLessLabel = this.viewMoreButton.dataset.viewLessLabel || 'View less';
    this.viewMoreExpanded = false;

    this.handleViewMoreClick = (event) => {
      event.preventDefault();
      this.viewMoreExpanded = !this.viewMoreExpanded;
      this.updateViewMoreState();
    };

    this.viewMoreButton.addEventListener('click', this.handleViewMoreClick);

    this.mobileQuery = window.matchMedia('(max-width: 989px)');
    this.handleMediaChange = () => this.updateViewMoreState();
    this.mobileQuery.addEventListener('change', this.handleMediaChange);

    this.updateViewMoreState();
  }

  updateViewMoreState() {
    if (!this.viewMoreButton) {
      return;
    }

    const isMobile = this.mobileQuery && this.mobileQuery.matches;

    this.items.forEach((item, index) => {
      item.classList.toggle(
        'figma-faq-accordion__item--hidden-mobile',
        isMobile && !this.viewMoreExpanded && index >= this.viewMoreThreshold
      );
    });

    if (isMobile) {
      this.viewMoreButton.hidden = false;
      this.viewMoreButton.textContent = this.viewMoreExpanded ? this.viewLessLabel : this.viewMoreLabel;
      this.viewMoreButton.setAttribute('aria-expanded', this.viewMoreExpanded ? 'true' : 'false');
    } else {
      this.viewMoreButton.hidden = true;
    }
  }
}

if (!customElements.get('figma-faq-accordion')) {
  customElements.define('figma-faq-accordion', FigmaFaqAccordion);
}